import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { holdCountdown } from '../lib/format';
import { useOnline } from '../lib/use-countdown';

/** Under this many seconds a valid hold reads as expiring, not merely counting. */
const EXPIRING_SOON_SECONDS = 60;

export type QuoteState = 'quoting' | 'valid' | 'expiring' | 'expired' | 'offline';

/**
 * Quote presentation is exactly five states (review §5): quoting, valid,
 * expiring-soon, expired, offline. The countdown must read as a price hold
 * ("Price held for 09:59"), and the quote id is copyable metadata — never a
 * headline the driver has to parse.
 */
export function QuoteStatus({
  quoteId,
  remaining,
  onRequote,
}: {
  /** null while no quote is held — the quoting state. */
  quoteId: string | null;
  /** Seconds left on the hold; meaningless unless quoteId is set. */
  remaining: number;
  /** Expired-state tap: discard the dead quote and request a fresh one. */
  onRequote?: () => void;
}) {
  const { t } = useTranslation();
  const online = useOnline();
  const state: QuoteState = !online
    ? 'offline'
    : !quoteId
      ? 'quoting'
      : remaining <= 0
        ? 'expired'
        : remaining <= EXPIRING_SOON_SECONDS
          ? 'expiring'
          : 'valid';

  return (
    <div className="flex min-h-11 items-center justify-between gap-3 text-xs">
      {state === 'offline' ? (
        <span className="font-medium text-warning">{t('duration.state.offline')}</span>
      ) : state === 'quoting' ? (
        <span className="animate-pulse text-muted">{t('duration.repricing')}</span>
      ) : state === 'expired' ? (
        // Secondary escape hatch, not the screen's primary action — the CTA is.
        <button type="button" onClick={onRequote} className="min-h-11 text-left font-semibold text-danger">
          {t('duration.state.expired')}
        </button>
      ) : state === 'expiring' ? (
        <span className="num font-semibold text-warning">
          {t('duration.state.expiringSoon', { time: holdCountdown(remaining) })}
        </span>
      ) : (
        <span className="num font-medium text-success">
          {t('duration.state.held', { time: holdCountdown(remaining) })}
        </span>
      )}
      {quoteId ? <CopyableQuoteId id={quoteId} /> : null}
    </div>
  );
}

/** The full id goes to the clipboard; only a short prefix is worth pixels. */
function CopyableQuoteId({ id }: { id: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(id);
    } catch {
      // Clipboard needs a secure context; without one there is nothing to
      // confirm, so no false "copied" feedback.
      return;
    }
    setCopied(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      type="button"
      aria-label={t('duration.copyQuoteId')}
      onClick={() => void copy()}
      className="num flex min-h-11 items-center text-[11px] text-muted"
    >
      {copied ? (
        <span className="text-success">{t('receipt.copied')}</span>
      ) : (
        `#${id.slice(0, 8).toUpperCase()}`
      )}
    </button>
  );
}
